import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MessageSquare, Bell, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { CalendarMockup, ChatMockup, AlertsMockup } from './FeatureMockups';

type FeatureSlide = {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  mockup: React.ReactNode;
  actionText: string;
  actionPath: string;
};

const features: FeatureSlide[] = [
  {
    id: 1,
    title: 'Economic Calendar',
    description: 'Stay ahead of market-moving events with our comprehensive calendar showing key economic releases and their expected impact.',
    icon: <Calendar className="h-6 w-6 text-blue-400" />,
    color: 'bg-blue-500/10',
    mockup: <CalendarMockup />,
    actionText: 'View Calendar',
    actionPath: '/calendar'
  },
  {
    id: 2,
    title: 'Market Alerts',
    description: 'Get notified about important market movements and breaking news that could affect your investments.',
    icon: <Bell className="h-6 w-6 text-amber-400" />,
    color: 'bg-amber-500/10',
    mockup: <AlertsMockup />,
    actionText: 'See Alerts',
    actionPath: '/alerts'
  },
  {
    id: 3,
    title: 'AI Market Analyst',
    description: 'Ask questions about any market in plain English and get detailed, AI-powered analysis and insights.',
    icon: <MessageSquare className="h-6 w-6 text-emerald-400" />,
    color: 'bg-emerald-500/10',
    mockup: <ChatMockup />,
    actionText: 'Try AI Chat',
    actionPath: '/chat'
  }
];

interface WelcomeCarouselProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WelcomeCarousel({ open, onOpenChange }: WelcomeCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const feature = features[currentSlide];
  const navigate = useNavigate();
  
  // Function to handle "Don't show again" behavior
  const handleDontShowAgain = () => {
    localStorage.setItem('welcomeCarouselShown', 'true');
    onOpenChange(false);
  };
  
  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 bg-gray-900/95 border-gray-800 shadow-2xl overflow-hidden hide-close-button max-h-[95vh] max-w-[95vw] w-full">
        <DialogHeader className="p-3 sm:p-6 flex-row justify-between items-center border-b border-gray-800/60">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${feature.color}`}>
              {feature.icon}
            </div>
            <DialogTitle className="text-lg sm:text-xl font-semibold">{feature.title}</DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-4">
          <div className="p-3 sm:p-6 md:p-8 order-2 md:order-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6">
                  {feature.description}
                </p>
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg text-sm sm:text-base"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(feature.actionPath);
                  }}
                >
                  {feature.actionText}
                </Button>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="p-2 sm:p-4 bg-gray-950/50 order-1 md:order-2 overflow-hidden max-h-[30vh] md:max-h-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="p-1 sm:p-2"
              >
                {feature.mockup}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        
        <DialogFooter className="p-3 sm:p-4 px-4 sm:px-6 border-t border-gray-800/60 flex items-center justify-between">
          <Button 
            variant="ghost" 
            className="text-xs sm:text-sm text-gray-400 hover:text-gray-300 px-2 sm:px-3"
            onClick={handleDontShowAgain}
          >
            Don't show again
          </Button>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {/* If on the last slide, show a Finish button instead of navigation */}
            {currentSlide === features.length - 1 ? (
              <motion.div
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
              >
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg transition-all duration-300 text-sm sm:text-base px-3 sm:px-4"
                  onClick={() => onOpenChange(false)}
                >
                  Finish
                </Button>
              </motion.div>
            ) : (
              <>
                <div className="text-xs sm:text-sm text-gray-400 mr-2 sm:mr-3">
                  {currentSlide + 1} of {features.length}
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={prevSlide}
                  className="h-7 w-7 sm:h-8 sm:w-8 border-gray-700"
                  disabled={currentSlide === 0}
                >
                  <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={nextSlide}
                  className="h-7 w-7 sm:h-8 sm:w-8 border-gray-700"
                >
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
